import { ReactNode, useMemo } from 'react'

import invariant from 'tiny-invariant'

import {
  Text,
  IconAgent,
  GreyGrey70,
  Primary13SemiBold
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { NodeTimeline } from 'Automations/components'
import {
  ExecutionSummaryComponent,
  ExecutionSummaryComponentProps
} from 'Automations/nodeDefinitions/types'
import {
  CancelledItem,
  FailedItem,
  hasCancelledTask,
  hasFailedTask,
  TerminalStateItem
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { isAutomationPresentation } from 'Automations/nodeDefinitions/guards'
import { parseTaskReferenceName } from 'Automations/nodeDefinitions/conductor'
import { useUpstreamConditionalProperties } from './hooks/useUpstreamConditionalProperties'
import { formatGroupLabel } from './helpers'
import type { Config } from './switch.types'
import { ELSE_HANDLE_LABEL } from './switch.constants'

export const SwitchExecutionSummary: ExecutionSummaryComponent = ({
  tasks,
  automationExecution,
  isLatestNodeExecution
}) => {
  const { caseLabel, taskInProgress, taskFailed, taskCancelled } =
    useSwitchData({
      tasks,
      automationExecution
    })

  if (taskCancelled) {
    return (
      <CancelledItem
        data-testid="automations.executionSummary.switch.cancelled"
        label="Switch evaluation cancelled"
        timestamp={automationExecution.createTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  if (taskInProgress) {
    return (
      <TerminalStateItem
        data-testid="automations.executionSummary.switch.inProgress"
        icon={<IconAgent />}
        label="Switch is being evaluated..."
        timestamp={automationExecution.createTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  if (taskFailed) {
    return (
      <FailedItem
        data-testid="automations.executionSummary.switch.failed"
        label="Failed to evaluate switch"
        timestamp={automationExecution.createTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  return (
    <NodeTimeline.Item
      data-testid="automations.executionSummary.switch.matched"
      icon={<IconAgent />}
      showStem
    >
      <NodeTimeline.Title>
        <NodeTimeline.UserAction
          label={
            <>
              Condition matched:{' '}
              <Text
                as="span"
                data-testid="automations.executionSummary.switch.caseLabel"
                {...Primary13SemiBold}
                color={GreyGrey70}
                overflowWrap="anywhere"
              >
                {caseLabel}
              </Text>
            </>
          }
          timestamp={automationExecution.createTime}
        />
      </NodeTimeline.Title>
    </NodeTimeline.Item>
  )
}

type UseSwitchData = (params: {
  tasks: ExecutionSummaryComponentProps['tasks']
  automationExecution: ExecutionSummaryComponentProps['automationExecution']
}) => {
  taskInProgress: boolean
  taskFailed: boolean
  taskCancelled: boolean
  caseLabel: ReactNode
}

const useSwitchData: UseSwitchData = ({ tasks, automationExecution }) => {
  // A switch node's tasks aren't just the switch: conditions can inject
  // supporting fetches (plus a fork/join pair) that share this node's id
  // prefix, and the execution reports them in its own order. Select the
  // switch by its parsed task name rather than by position.
  const switchTask = useMemo(
    () =>
      tasks.find(
        task =>
          parseTaskReferenceName(task.taskReferenceName).taskName === 'switch'
      ),
    [tasks]
  )

  const automationVersionQuery = useQuery(
    dashboardv99990101Client.getAutomationVersion,
    {
      automationId: automationExecution.automationId,
      versionId: automationExecution.automationVersionId
    }
  )

  // Every task reported for this node carries its id, so the node id resolves
  // even while the switch task itself is still pending behind a supporting
  // fetch.
  const anyNodeTask = tasks.at(0)
  invariant(anyNodeTask, 'switch node has no tasks')

  const selectedCase: string | undefined = useMemo(() => {
    return switchTask?.outputData?.selectedCase as string | undefined
  }, [switchTask])

  const versionPresentation = automationVersionQuery.data?.presentation
  const { nodeId: switchNodeId } = parseTaskReferenceName(
    anyNodeTask.taskReferenceName
  )
  const conditionalProperties = useUpstreamConditionalProperties(
    switchNodeId,
    isAutomationPresentation(versionPresentation)
      ? versionPresentation
      : undefined
  )

  const caseLabel: ReactNode = useMemo(() => {
    if (automationVersionQuery.isLoading) return ''

    if (selectedCase === 'UNKNOWN') return ELSE_HANDLE_LABEL

    if (!selectedCase || !isAutomationPresentation(versionPresentation)) {
      return 'Unknown'
    }

    const switchNode = versionPresentation.nodes.find(
      n => n.type === 'SWITCH' && n.id === switchNodeId
    )
    if (!switchNode) return 'Unknown'

    const groups = (switchNode.data as Config).groups
    const group = groups.find(g => g.id === selectedCase)
    if (!group) return 'Unknown'

    if (!conditionalProperties) return group.label || 'Unknown'

    return formatGroupLabel(
      group,
      conditionalProperties,
      versionPresentation.nodes
    )
  }, [
    selectedCase,
    switchNodeId,
    versionPresentation,
    conditionalProperties,
    automationVersionQuery.isLoading
  ])

  // Spans every task of the node, so a supporting fetch that fails or is
  // cancelled surfaces as the switch failing — it genuinely can't be evaluated.
  const taskFailed = useMemo(() => hasFailedTask(tasks), [tasks])

  const taskCancelled = useMemo(() => hasCancelledTask(tasks), [tasks])

  // No switch task yet means its supporting fetches are still running, which
  // reads as the switch not having been evaluated yet — unless one of them
  // already reached a terminal state.
  const taskInProgress = useMemo(() => {
    if (taskFailed || taskCancelled) return false
    return !switchTask || switchTask.status === 'IN_PROGRESS'
  }, [switchTask, taskFailed, taskCancelled])

  return {
    taskInProgress,
    taskFailed,
    taskCancelled,
    caseLabel
  }
}
